import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Eye, Database, Users, Check } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';

interface PrivacyPolicyProps {
  onAccept?: () => void;
  isAccepted?: boolean;
}

const sections = [
  {
    id: 'data-collection',
    title: 'What We Collect',
    icon: Database,
    content: [
      {
        category: 'Profile Information',
        items: ['Name, email, and basic demographics', 'Relationship preferences and goals', 'Communication style assessments']
      },
      {
        category: 'Interaction Data',
        items: ['Mood logs and emotional check-ins', 'Chat history with AI assistant', 'Progress tracking and milestone achievements']
      },
      {
        category: 'Wearable Integration',
        items: ['Heart rate variability patterns (not raw data)', 'Sleep quality correlations', 'Stress level indicators']
      }
    ]
  },
  {
    id: 'data-usage',
    title: 'How We Use Your Data',
    icon: Eye,
    content: [
      {
        category: 'AI Insights',
        items: ['Generate personalized relationship advice', 'Identify communication patterns', 'Suggest improvement strategies']
      },
      {
        category: 'Progress Tracking',
        items: ['Monitor relationship health trends', 'Celebrate milestones and achievements', 'Provide weekly insight reports']
      },
      {
        category: 'Community Features',
        items: ['Anonymous trend sharing (opt-in)', 'Peer support matching', 'Educational content recommendations']
      }
    ]
  },
  {
    id: 'third-party',
    title: 'Third-Party Services',
    icon: Users,
    content: [
      {
        category: 'Essential Services',
        items: ['Supabase: Secure authentication and data storage', 'RevenueCat: Subscription management', 'Google Gemini: AI-powered insights']
      },
      {
        category: 'Enhanced Features',
        items: ['ElevenLabs: Text-to-speech for accessibility', 'Tavus: AI avatar interactions', 'Apple HealthKit/Google Fit: Wearable data']
      }
    ]
  },
  {
    id: 'user-rights',
    title: 'Your Rights',
    icon: Shield,
    content: [
      {
        category: 'Data Control',
        items: ['Access all your stored data', 'Download your information', 'Delete your account and data']
      },
      {
        category: 'Privacy Options',
        items: ['Opt-out of data sharing', 'Disable wearable integration', 'Control AI training participation']
      }
    ]
  }
];

export const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onAccept, isAccepted = false }) => {
  const [activeSection, setActiveSection] = useState(0);
  const [hasScrolled, setHasScrolled] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100 && !hasScrolled) {
        setHasScrolled(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasScrolled]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-blue-50 to-teal-100 dark:from-purple-900/20 dark:via-blue-900/20 dark:to-teal-900/20 pt-20">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute inset-0 opacity-30"
          animate={prefersReducedMotion ? {} : {
            background: [
              "radial-gradient(circle at 20% 80%, #8B5CF6 0%, transparent 50%)",
              "radial-gradient(circle at 80% 20%, #06B6D4 0%, transparent 50%)",
              "radial-gradient(circle at 40% 40%, #10B981 0%, transparent 50%)",
            ]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Your privacy is our priority. Here's how we protect and use your data to help you build better relationships.
          </p>
        </motion.div>

        {/* Key Principles */}
        <motion.div
          className="mb-12 p-8 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Our Core Principles
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                title: 'Privacy First',
                description: 'We never store raw biometric data – only correlations are analyzed.'
              },
              {
                icon: Eye,
                title: 'Transparency',
                description: 'You always know what data we collect and how it\'s used.'
              },
              {
                icon: Users,
                title: 'Your Control',
                description: 'Access, modify, or delete your data anytime with one click.'
              }
            ].map((principle, index) => (
              <motion.div
                key={principle.title}
                className="text-center"
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full mb-4">
                  <principle.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {principle.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  {principle.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Detailed Sections */}
        <div className="space-y-8">
          {sections.map((section, sectionIndex) => (
            <motion.div
              key={section.id}
              className="p-8 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20"
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: sectionIndex * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full">
                  <section.icon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {section.title}
                </h2>
              </div>

              <div className="space-y-6">
                {section.content.map((category, categoryIndex) => (
                  <motion.div
                    key={category.category}
                    initial={prefersReducedMotion ? {} : { opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      {category.category}
                    </h3>
                    <ul className="space-y-2">
                      {category.items.map((item, itemIndex) => (
                        <motion.li
                          key={item}
                          className="flex items-start gap-3"
                          initial={prefersReducedMotion ? {} : { opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: itemIndex * 0.05 }}
                          viewport={{ once: true }}
                        >
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600 dark:text-gray-300">{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Contact Information */}
        <motion.div
          className="mt-12 p-8 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20 text-center"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Questions About Your Privacy?
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            We're here to help. Contact our privacy team anytime.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:privacy@octify.app"
              className="px-6 py-3 bg-white/10 dark:bg-gray-800/10 text-gray-900 dark:text-white rounded-full font-medium hover:bg-white/20 dark:hover:bg-gray-800/20 transition-colors duration-200"
            >
              privacy@octify.app
            </a>
            <a
              href="#contact"
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full font-medium hover:shadow-lg transition-all duration-200"
            >
              Contact Support
            </a>
          </div>
        </motion.div>

        {/* Last Updated */}
        <div className="mt-12 text-center text-sm text-gray-500 dark:text-gray-400">
          Last updated: January 2025
        </div>
      </div>
    </div>
  );
};